import { useEffect, useLayoutEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { StandaloneWidget } from "@/components/widget/StandaloneWidget";
import { ChatWidget } from "@/components/widget/ChatWidget";

// Identity can be supplied on the standalone widget URL, e.g.
//   /widget/KEY?name=Jane&email=j@x.com&phone=+91...&uid=USER_ID&hash=HMAC
function identityFromParams(params) {
  const id = {
    name: params.get("name") || undefined,
    email: params.get("email") || undefined,
    phone: params.get("phone") || undefined,
    location: params.get("location") || params.get("loc") || undefined,
    userId: params.get("uid") || params.get("user_id") || undefined,
    userHash: params.get("hash") || params.get("user_hash") || undefined,
  };
  return Object.values(id).some(Boolean) ? id : null;
}

export default function WidgetPage() {
  const { apiKey } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const showLauncher = params.get("launcher") !== "0";
  const identity = useMemo(() => identityFromParams(params), [params]);

  useLayoutEffect(() => {
    if (embed) {
      document.documentElement.classList.add("superchat-embed");
      document.documentElement.style.background = "transparent";
      document.body.style.background = "transparent";
      return () => document.documentElement.classList.remove("superchat-embed");
    }
    // Lock the page so the widget behaves like a native full-screen chat app
    // on mobile (no rubber-band scroll behind the chat).
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [embed]);

  // Full-screen widget (e.g. opened in an app webview): trap the hardware back
  // button so it doesn't exit the chat. In embed mode the host page's embed.js
  // handles back instead.
  useEffect(() => {
    if (embed) return;
    try { window.history.pushState(null, ""); } catch {}
    const onPop = () => { try { window.history.pushState(null, ""); } catch {} };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [embed]);

  // Restrict copy/selection and zoom — for the VISITOR WIDGET only. Agents and
  // admins are never affected (they can copy customer details freely).
  useEffect(() => {
    document.body.classList.add("sc-restrict");
    const vp = document.querySelector('meta[name="viewport"]');
    const prevVp = vp ? vp.getAttribute("content") : null;
    if (vp) vp.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover, interactive-widget=resizes-content");

    const stop = (e) => e.preventDefault();
    const noCtrlWheel = (e) => { if (e.ctrlKey) e.preventDefault(); };
    const noZoomKey = (e) => { if ((e.ctrlKey || e.metaKey) && ["+", "-", "=", "0"].includes(e.key)) e.preventDefault(); };
    let lastTouch = 0;
    const noDoubleTap = (e) => { if (e.timeStamp - lastTouch <= 300) e.preventDefault(); lastTouch = e.timeStamp; };
    document.addEventListener("gesturestart", stop, { passive: false });
    document.addEventListener("gesturechange", stop, { passive: false });
    document.addEventListener("wheel", noCtrlWheel, { passive: false });
    document.addEventListener("keydown", noZoomKey);
    document.addEventListener("touchend", noDoubleTap, { passive: false });

    return () => {
      document.body.classList.remove("sc-restrict");
      if (vp && prevVp) vp.setAttribute("content", prevVp);
      document.removeEventListener("gesturestart", stop);
      document.removeEventListener("gesturechange", stop);
      document.removeEventListener("wheel", noCtrlWheel);
      document.removeEventListener("keydown", noZoomKey);
      document.removeEventListener("touchend", noDoubleTap);
    };
  }, []);

  if (embed) {
    return (
      <div className="h-screen w-screen bg-transparent">
        <ChatWidget apiKey={apiKey} embed urlIdentity={identity} showLauncher={showLauncher} />
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[#0a1512] p-0 sm:p-8">
      <div className="font-wa h-[100dvh] w-full overflow-hidden bg-white shadow-2xl sm:h-[820px] sm:max-h-[94vh] sm:w-[400px] sm:rounded-[30px] sm:ring-[10px] sm:ring-black/50">
        <StandaloneWidget apiKey={apiKey} identity={identity} />
      </div>
    </div>
  );
}
